import {
  SvgIconNames,
  SvgIconsMap,
} from '@design/design-tokens/iconography/SvgIcons';
import React, { HTMLAttributes } from 'react';

export interface IconProps extends HTMLAttributes<SVGSVGElement> {
  /** Name of the icon to render, from the design-system `SvgIconNames` set. */
  iconName: SvgIconNames;
  /** Class forwarded to the SVG element. */
  className?: string;
  /** Square dimensions in pixels. Used as the default for both width and height. Defaults to `40`. */
  size?: number;
  /** Width override (in pixels). Falls back to `size` when omitted. */
  width?: number;
  /** Height override (in pixels). Falls back to `size` when omitted. */
  height?: number;
  /**
   * SVG `viewBox`. Defaults to `'0 0 40 40'`. Only override if the
   * underlying SVG asset uses a different viewBox.
   */
  viewBox?: string;
  /** SVG `fill`. Defaults to the design token `var(--icon-default)`. */
  fillColor?: string;
  /** SVG `stroke`. Most icons rely on `fill`; set this only for line-style icons. */
  strokeColor?: string;
}

/**
 * Renders a named SVG icon from the design-system iconography set.
 *
 * Use `iconName` to pick the symbol (autocompleted via `SvgIconNames`).
 * Pass `size` for a square icon, or `width`/`height` independently.
 * Returns `null` when `iconName` doesn't match a registered SVG.
 */
export const Icon = ({
  iconName,
  className,
  size = 40,
  width,
  height,
  viewBox = '0 0 40 40',
  fillColor = 'var(--icon-default)',
  strokeColor,
  ...rest
}: IconProps) => {
  const SVGIcon = SvgIconsMap[iconName];
  return (
    <>
      {SVGIcon ? (
        <SVGIcon
          width={width ?? size}
          height={height ?? size}
          viewBox={viewBox}
          fill={fillColor}
          stroke={strokeColor}
          className={className}
          data-testid={`${iconName}-icon`}
          xmlns="http://www.w3.org/2000/svg"
          {...rest}
        />
      ) : null}
    </>
  );
};
